import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SectorsService } from '../../sectors/sectors.service';
import { BranchNames, SectorNames } from '../../sectors/sectors';
import { NormativesService } from '../normatives.service';
import { Region } from '../normatives';
import { IStandardsResponse, IFederalStandard, IRegionStandard, ISectorStandard } from '../normatives';
import { of, Observable, forkJoin } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmModalComponent } from '../../../shared/modals/confirm-modal/confirm-modal.component';
import { INormativeCell, INormativeRow, IUpdateResponse } from '../normatives';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-normatives',
  templateUrl: './normatives.component.html',
  styleUrls: ['./normatives.component.scss'],
  standalone: false,
})
export class NormativesComponent implements OnInit {
  public regions: Region[] = [];
  public branches: BranchNames[] = [];
  public sectors: SectorNames[] = [];

  public selectedRegion: Region | null = null;
  public selectedBranch: BranchNames | null = null;
  public selectedSector: SectorNames | null = null;

  public appliedRegion: Region | null = null;
  public appliedBranch: BranchNames | null = null;
  public appliedSector: SectorNames | null = null;

  public isLoading = false;
  public isFilterApplied = false;

  public rfTableHeaders: string[] = [];
  public rfTableRows: INormativeRow[] = [];
  public federalTableHeaders: string[] = [];
  public federalTableRows: INormativeRow[] = [];
  public branchTableHeaders: string[] = [];
  public branchTableRows: INormativeRow[] = [];
  public sectorTableHeaders: string[] = [];
  public sectorTableRows: INormativeRow[] = [];

  // Переменные для отслеживания offset для каждой таблицы
  private rfOffset = 0;
  private federalOffset = 0;
  private branchOffset = 0;
  private sectorOffset = 0;

  // Переменные для отслеживания, есть ли еще данные для загрузки (теперь заглушки)
  public rfHasMoreData = true;
  public federalHasMoreData = true;
  public branchHasMoreData = true;
  public sectorHasMoreData = true;

  constructor(
    private sectorsService: SectorsService,
    private normativesService: NormativesService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.loadInitialData();
  }

  private loadInitialData() {
    this.isLoading = true;
    this.sectorsService.getRegions().subscribe({
      next: (regionsResponse) => {
        this.regions = regionsResponse.data.map((name) => ({ name }));
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
    this.loadRfStandardsBulk();
  }

  // Новый метод для массовой загрузки RF стандартов до пустого массива
  private loadRfStandardsBulk() {
    this.isLoading = true;
    const limit = 30;
    let allStandards: any[] = [];
    let currentOffset = 0;

    const loadNextBatch = () => {
      this.isLoading = true;
      this.normativesService.getCountryStandards(limit, currentOffset).subscribe({
        next: (response) => {
          if (response.standards && response.standards.length > 0) {
            // Добавляем полученные данные к общему массиву
            allStandards = allStandards.concat(response.standards);
            currentOffset += limit;

            // Продолжаем загрузку следующего батча с небольшой задержкой
            setTimeout(() => loadNextBatch(), 0);
          } else {
            // Массив пустой - завершаем загрузку
            if (allStandards.length > 0) {
              const transformedData = this.normativesService.transformStandardsToPivotTable(allStandards);
              this.rfTableHeaders = transformedData.headers;
              this.rfTableRows = transformedData.rows;
              this.rfOffset = currentOffset;
            }
            this.isLoading = false;
          }
        },
        error: () => {
          // В случае ошибки завершаем загрузку с тем, что есть
          if (allStandards.length > 0) {
            const transformedData = this.normativesService.transformStandardsToPivotTable(allStandards);
            this.rfTableHeaders = transformedData.headers;
            this.rfTableRows = transformedData.rows;
            this.rfOffset = currentOffset;
          }
          this.isLoading = false;
        },
      });
    };

    // Начинаем загрузку с первого батча с небольшой задержкой для отображения спиннера
    setTimeout(() => loadNextBatch(), 0);
  }

  // Новый метод для массовой загрузки федеральных стандартов до пустого массива
  private loadFederalStandardsBulk(regionName: string) {
    this.isLoading = true;
    const limit = 30;
    let allStandards: IFederalStandard[] = [];
    let currentOffset = 0;

    const loadNextBatch = () => {
      this.isLoading = true;
      this.normativesService.getFederalStandards(regionName, limit, currentOffset).subscribe({
        next: (response) => {
          if (response.standards && response.standards.length > 0) {
            // Добавляем полученные данные к общему массиву
            allStandards = allStandards.concat(response.standards);
            currentOffset += limit;

            // Продолжаем загрузку следующего батча с небольшой задержкой
            setTimeout(() => loadNextBatch(), 0);
          } else {
            // Массив пустой - завершаем загрузку
            if (allStandards.length > 0) {
              const transformedData = this.normativesService.transformStandardsToPivotTable(allStandards);
              this.federalTableHeaders = transformedData.headers;
              this.federalTableRows = transformedData.rows;
              this.federalOffset = currentOffset;
            } else {
              this.federalTableHeaders = [];
              this.federalTableRows = [];
            }
            this.isLoading = false;
          }
        },
        error: () => {
          // В случае ошибки завершаем загрузку с тем, что есть
          if (allStandards.length > 0) {
            const transformedData = this.normativesService.transformStandardsToPivotTable(allStandards);
            this.federalTableHeaders = transformedData.headers;
            this.federalTableRows = transformedData.rows;
            this.federalOffset = currentOffset;
          }
          this.isLoading = false;
        },
      });
    };

    // Начинаем загрузку с первого батча с небольшой задержкой для отображения спиннера
    setTimeout(() => loadNextBatch(), 0);
  }

  // Новый метод для массовой загрузки региональных стандартов до пустого массива
  private loadBranchStandardsBulk(branchId: number) {
    this.isLoading = true;
    const limit = 30;
    let allStandards: IRegionStandard[] = [];
    let currentOffset = 0;

    const loadNextBatch = () => {
      this.isLoading = true;
      this.normativesService.getRegionStandards(branchId, limit, currentOffset).subscribe({
        next: (response) => {
          if (response.standards && response.standards.length > 0) {
            // Добавляем полученные данные к общему массиву
            allStandards = allStandards.concat(response.standards);
            currentOffset += limit;

            // Продолжаем загрузку следующего батча с небольшой задержкой
            setTimeout(() => loadNextBatch(), 0);
          } else {
            // Массив пустой - завершаем загрузку
            if (allStandards.length > 0) {
              const transformedData = this.normativesService.transformStandardsToPivotTable(allStandards);
              this.branchTableHeaders = transformedData.headers;
              this.branchTableRows = transformedData.rows;
              this.branchOffset = currentOffset;
            } else {
              this.branchTableHeaders = [];
              this.branchTableRows = [];
            }
            this.isLoading = false;
          }
        },
        error: () => {
          // В случае ошибки завершаем загрузку с тем, что есть
          if (allStandards.length > 0) {
            const transformedData = this.normativesService.transformStandardsToPivotTable(allStandards);
            this.branchTableHeaders = transformedData.headers;
            this.branchTableRows = transformedData.rows;
            this.branchOffset = currentOffset;
          }
          this.isLoading = false;
        },
      });
    };

    // Начинаем загрузку с первого батча с небольшой задержкой для отображения спиннера
    setTimeout(() => loadNextBatch(), 0);
  }

  // Новый метод для массовой загрузки секторных стандартов до пустого массива
  private loadSectorStandardsBulk(branchId: number, sectorId: number) {
    this.isLoading = true;
    const limit = 30;
    let allStandards: ISectorStandard[] = [];
    let currentOffset = 0;

    const loadNextBatch = () => {
      this.isLoading = true;
      this.normativesService.getSectorStandards(branchId, sectorId, limit, currentOffset).subscribe({
        next: (response) => {
          if (response.standards && response.standards.length > 0) {
            // Добавляем полученные данные к общему массиву
            allStandards = allStandards.concat(response.standards);
            currentOffset += limit;

            // Продолжаем загрузку следующего батча с небольшой задержкой
            setTimeout(() => loadNextBatch(), 0);
          } else {
            // Массив пустой - завершаем загрузку
            if (allStandards.length > 0) {
              const transformedData = this.normativesService.transformStandardsToPivotTable(allStandards);
              this.sectorTableHeaders = transformedData.headers;
              this.sectorTableRows = transformedData.rows;
              this.sectorOffset = currentOffset;
            } else {
              this.sectorTableHeaders = [];
              this.sectorTableRows = [];
            }
            this.isLoading = false;
          }
        },
        error: () => {
          // В случае ошибки завершаем загрузку с тем, что есть
          if (allStandards.length > 0) {
            const transformedData = this.normativesService.transformStandardsToPivotTable(allStandards);
            this.sectorTableHeaders = transformedData.headers;
            this.sectorTableRows = transformedData.rows;
            this.sectorOffset = currentOffset;
          }
          this.isLoading = false;
        },
      });
    };

    // Начинаем загрузку с первого батча с небольшой задержкой для отображения спиннера
    setTimeout(() => loadNextBatch(), 0);
  }

  // Старые методы для одиночной загрузки (оставляем для совместимости, но не используем)
  private loadRfStandards(loadMore: boolean = false) {
    this.isLoading = true;
    const limit = 30;
    const offset = loadMore ? this.rfOffset : 0;

    if (!loadMore) {
      this.rfOffset = 0;
    }

    this.normativesService.getCountryStandards(limit, offset).subscribe({
      next: (response) => {
        if (response.standards) {
          const transformedData = this.normativesService.transformStandardsToPivotTable(response.standards);

          if (loadMore) {
            // Добавляем новые строки к существующим
            this.rfTableRows = this.rfTableRows.concat(transformedData.rows);
          } else {
            // Первая загрузка - заменяем данные
            this.rfTableHeaders = transformedData.headers;
            this.rfTableRows = transformedData.rows;
          }

          // Обновляем offset для следующего запроса
          this.rfOffset += limit;

          // Проверяем, есть ли еще данные (если пришло меньше 30 записей, значит больше нет)
          this.rfHasMoreData = response.standards.length === limit;
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  public onChangeRegion(region: Region | null) {
    this.selectedRegion = region;
    this.selectedBranch = null;
    this.branches = [];
    this.selectedSector = null;
    this.sectors = [];

    if (region) {
      this.isLoading = true;
      this.sectorsService.getBranches({ mb_regions: [region.name] }).subscribe({
        next: (branchesResponse) => {
          this.branches = branchesResponse.data;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
    }
  }

  public onChangeBranch(branch: BranchNames | null) {
    this.selectedBranch = branch;
    this.selectedSector = null;
    this.sectors = [];

    if (branch) {
      this.isLoading = true;
      this.sectorsService.getFilterPlots({ mb_ids: [branch.mb_id] }).subscribe({
        next: (sectorsResponse) => {
          this.sectors = this.sectorsService.makeSectorsNumbers(sectorsResponse.data.filter((sector) => sector.is_visible !== 0));
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
    }
  }

  public onChangeSector(sector: SectorNames | null) {
    this.selectedSector = sector;
  }

  public applyFilter() {
    this.isFilterApplied = true;
    this.appliedRegion = this.selectedRegion;
    this.appliedBranch = this.selectedBranch;
    this.appliedSector = this.selectedSector;

    this.federalTableRows = [];
    this.federalTableHeaders = [];
    this.branchTableRows = [];
    this.branchTableHeaders = [];
    this.sectorTableRows = [];
    this.sectorTableHeaders = [];

    // Сброс offset и флагов для фильтруемых таблиц
    this.federalOffset = 0;
    this.branchOffset = 0;
    this.sectorOffset = 0;
    this.federalHasMoreData = true;
    this.branchHasMoreData = true;
    this.sectorHasMoreData = true;

    if (this.appliedRegion) {
      this.loadFederalStandardsBulk(this.appliedRegion.name);
    }
    if (this.appliedBranch) {
      this.loadBranchStandardsBulk(this.appliedBranch.mb_id);
    }
    if (this.appliedBranch && this.appliedSector) {
      this.loadSectorStandardsBulk(this.appliedBranch.mb_id, this.appliedSector.sector_id);
    }
  }

  private loadFederalStandards(regionName: string, loadMore: boolean = false) {
    this.isLoading = true;
    const limit = 30;
    const offset = loadMore ? this.federalOffset : 0;

    if (!loadMore) {
      this.federalOffset = 0;
    }

    this.normativesService
      .getFederalStandards(regionName, limit, offset)
      .pipe(
        catchError(() => {
          if (!loadMore) {
            this.federalTableHeaders = [];
            this.federalTableRows = [];
          }
          return of({ standards: [] } as IStandardsResponse<IFederalStandard>);
        })
      )
      .subscribe({
        next: (response) => {
          if (response.standards) {
            const transformedData = this.normativesService.transformStandardsToPivotTable(response.standards);

            if (loadMore) {
              this.federalTableRows = this.federalTableRows.concat(transformedData.rows);
            } else {
              this.federalTableHeaders = transformedData.headers;
              this.federalTableRows = transformedData.rows;
            }

            this.federalOffset += limit;
            this.federalHasMoreData = response.standards.length === limit;
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private loadBranchStandards(branchId: number, loadMore: boolean = false) {
    this.isLoading = true;
    const limit = 30;
    const offset = loadMore ? this.branchOffset : 0;

    if (!loadMore) {
      this.branchOffset = 0;
    }

    this.normativesService
      .getRegionStandards(branchId, limit, offset)
      .pipe(
        catchError(() => {
          if (!loadMore) {
            this.branchTableHeaders = [];
            this.branchTableRows = [];
          }
          return of({ standards: [] } as IStandardsResponse<IRegionStandard>);
        })
      )
      .subscribe({
        next: (response) => {
          if (response.standards) {
            const transformedData = this.normativesService.transformStandardsToPivotTable(response.standards);

            if (loadMore) {
              this.branchTableRows = this.branchTableRows.concat(transformedData.rows);
            } else {
              this.branchTableHeaders = transformedData.headers;
              this.branchTableRows = transformedData.rows;
            }

            this.branchOffset += limit;
            this.branchHasMoreData = response.standards.length === limit;
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private loadSectorStandards(branchId: number, sectorId: number, loadMore: boolean = false) {
    this.isLoading = true;
    const limit = 30;
    const offset = loadMore ? this.sectorOffset : 0;

    if (!loadMore) {
      this.sectorOffset = 0;
    }

    this.normativesService
      .getSectorStandards(branchId, sectorId, limit, offset)
      .pipe(
        catchError(() => {
          if (!loadMore) {
            this.sectorTableHeaders = [];
            this.sectorTableRows = [];
          }
          return of({ standards: [] } as IStandardsResponse<ISectorStandard>);
        })
      )
      .subscribe({
        next: (response) => {
          if (response.standards) {
            const transformedData = this.normativesService.transformStandardsToPivotTable(response.standards);

            if (loadMore) {
              this.sectorTableRows = this.sectorTableRows.concat(transformedData.rows);
            } else {
              this.sectorTableHeaders = transformedData.headers;
              this.sectorTableRows = transformedData.rows;
            }

            this.sectorOffset += limit;
            this.sectorHasMoreData = response.standards.length === limit;
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  public handleOverwrite({ level, cells, cascade }: { level: string; cells: INormativeCell[]; cascade: boolean }): void {
    if (cells.length === 0) {
      return;
    }

    const modalRef = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    modalRef.componentInstance.title = cascade ? 'Перезаписать все нормативы' : 'Перезаписать нормативы';
    modalRef.componentInstance.description = 'Вы уверены, что хотите сохранить изменения?';
    modalRef.componentInstance.successButtonName = 'Перезаписать';
    modalRef.componentInstance.abortButtonName = 'Вернуться';

    modalRef.result.then(
      (result) => {
        if (result) {
          this.batchUpdateStandards(level, cells, cascade);
        }
      },
      () => {}
    );
  }

  private batchUpdateStandards(level: string, cells: INormativeCell[], cascade: boolean): void {
    this.isLoading = true;

    // Группируем ячейки по строкам для поиска итоговых ячеек
    const cellsByRow = this.groupCellsByRow(cells, level);

    // Создаем последовательность обновлений для каждой строки
    const updateSequences: Observable<any>[] = [];

    cellsByRow.forEach((rowCells, rowIndex) => {
      // Для каждой строки создаем последовательность: сначала обычные ячейки, потом итоговая
      const sequence = this.createUpdateSequenceForRow(level, rowCells, cascade, rowIndex);
      if (sequence) {
        updateSequences.push(sequence);
      }
    });

    if (updateSequences.length > 0) {
      // Выполняем все последовательности параллельно (но внутри каждой последовательности обновления идут последовательно)
      forkJoin(updateSequences)
        .pipe(
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe({
          next: () => {
            cells.forEach((cell) => {
              cell.isChanged = false;
              cell.originalValue = cell.value;
            });
          },
          error: (error) => {
            console.error('Ошибка при пакетном обновлении нормативов:', error);
          },
        });
    } else {
      this.isLoading = false;
    }
  }

  private groupCellsByRow(cells: INormativeCell[], level: string): Map<number, INormativeCell[]> {
    const cellsByRow = new Map<number, INormativeCell[]>();

    cells.forEach((cell) => {
      const rowIndex = this.findRowIndexForCell(cell, level);
      if (rowIndex !== -1) {
        if (!cellsByRow.has(rowIndex)) {
          cellsByRow.set(rowIndex, []);
        }
        cellsByRow.get(rowIndex)!.push(cell);
      }
    });

    return cellsByRow;
  }

  private findRowIndexForCell(cell: INormativeCell, level: string): number {
    let rows: INormativeRow[] = [];

    switch (level) {
      case 'country':
        rows = this.rfTableRows;
        break;
      case 'federal':
        rows = this.federalTableRows;
        break;
      case 'region':
        rows = this.branchTableRows;
        break;
      case 'sector':
        rows = this.sectorTableRows;
        break;
    }

    return rows.findIndex((row) => row.cells.some((rowCell) => rowCell && rowCell.id === cell.id));
  }

  private createUpdateSequenceForRow(level: string, rowCells: INormativeCell[], cascade: boolean, rowIndex: number): Observable<any> | null {
    if (rowCells.length === 0) return null;

    // Обновляем обычные ячейки
    const regularCellUpdates = rowCells
      .filter((cell) => cell.value !== null)
      .map((cell) => this.createSingleUpdateObservable(level, cell, cascade))
      .filter((obs) => obs !== null);

    if (regularCellUpdates.length === 0) return null;

    // После обновления обычных ячеек, обновляем итоговую ячейку
    return forkJoin(regularCellUpdates).pipe(
      switchMap(() => {
        // Находим и обновляем итоговую ячейку для этой строки
        const totalCell = this.findTotalCellForRow(level, rowIndex);

        if (totalCell) {
          // Пересчитываем итоговое значение
          const newTotalValue = this.calculateTotalForRow(level, rowIndex);
          totalCell.value = newTotalValue;

          // Обновляем итоговую ячейку на бэкенде
          const totalUpdateObservable = this.createSingleUpdateObservable(level, totalCell, cascade);

          if (totalUpdateObservable) {
            return totalUpdateObservable;
          }
        }
        return of(null);
      })
    );
  }

  private createSingleUpdateObservable(level: string, cell: INormativeCell, cascade: boolean): Observable<IUpdateResponse> | null {
    switch (level) {
      case 'country':
        return this.normativesService.updateCountryStandard(cell.id, cell.value, cascade);
      case 'federal':
        return this.normativesService.updateFederalStandard(cell.id, cell.value, cascade);
      case 'region':
        return this.normativesService.updateRegionStandard(cell.id, cell.value, cascade);
      case 'sector':
        return this.normativesService.updateSectorStandard(cell.id, cell.value, cascade);
      default:
        return null;
    }
  }

  private findTotalCellForRow(level: string, rowIndex: number): INormativeCell | null {
    let rows: INormativeRow[] = [];
    let headers: string[] = [];

    switch (level) {
      case 'country':
        rows = this.rfTableRows;
        headers = this.rfTableHeaders;
        break;
      case 'federal':
        rows = this.federalTableRows;
        headers = this.federalTableHeaders;
        break;
      case 'region':
        rows = this.branchTableRows;
        headers = this.branchTableHeaders;
        break;
      case 'sector':
        rows = this.sectorTableRows;
        headers = this.sectorTableHeaders;
        break;
    }

    if (rowIndex < 0 || rowIndex >= rows.length) {
      return null;
    }

    // Ищем индекс столбца "База норматив, мин."
    const totalColumnIndex = headers.findIndex((header) => header === 'База норматив, мин.');

    if (totalColumnIndex === -1) {
      return null;
    }

    // Учитываем, что первый столбец - это "Название работы", поэтому вычитаем 1
    // Но также нужно учесть, что массив cells содержит только ячейки данных (без названия работы)
    const cellIndex = totalColumnIndex - 1; // Вычитаем 1 для пропуска "Название работы"

    // Проверяем, что cellIndex находится в пределах массива cells
    // Если cellIndex >= cells.length, значит итоговая ячейка не существует в данных
    if (cellIndex < 0 || cellIndex >= rows[rowIndex].cells.length) {
      return null;
    }

    return rows[rowIndex].cells[cellIndex];
  }

  private calculateTotalForRow(level: string, rowIndex: number): number {
    let rows: INormativeRow[] = [];
    let headers: string[] = [];

    switch (level) {
      case 'country':
        rows = this.rfTableRows;
        headers = this.rfTableHeaders;
        break;
      case 'federal':
        rows = this.federalTableRows;
        headers = this.federalTableHeaders;
        break;
      case 'region':
        rows = this.branchTableRows;
        headers = this.branchTableHeaders;
        break;
      case 'sector':
        rows = this.sectorTableRows;
        headers = this.sectorTableHeaders;
        break;
    }

    if (rowIndex < 0 || rowIndex >= rows.length) return 0;

    const row = rows[rowIndex];
    let total = 0;

    // Суммируем все ячейки кроме итоговой (последней)
    const totalColumnIndex = headers.findIndex((header) => header === 'База норматив, мин.');
    const cellsToSum = row.cells.slice(0, totalColumnIndex - 1); // -1 потому что первый столбец - "Название работы"

    cellsToSum.forEach((cell) => {
      if (cell && cell.value !== null) {
        total += cell.value;
      }
    });

    return total;
  }

  public resetFilter() {
    this.selectedRegion = null;
    this.selectedBranch = null;
    this.selectedSector = null;
    this.branches = [];
    this.sectors = [];

    this.appliedRegion = null;
    this.appliedBranch = null;
    this.appliedSector = null;
    this.isFilterApplied = false;

    this.federalTableRows = [];
    this.federalTableHeaders = [];
    this.branchTableRows = [];
    this.branchTableHeaders = [];
    this.sectorTableRows = [];
    this.sectorTableHeaders = [];

    // Сброс offset и флагов
    this.rfOffset = 0;
    this.federalOffset = 0;
    this.branchOffset = 0;
    this.sectorOffset = 0;
    this.rfHasMoreData = true;
    this.federalHasMoreData = true;
    this.branchHasMoreData = true;
    this.sectorHasMoreData = true;

    this.loadRfStandardsBulk();
  }

  // Методы для кнопок "Показать еще" (заглушки)
  public loadMoreRfStandards(): void {
    console.log('Загрузка дополнительных RF стандартов (заглушка)');
    // Заглушка - ничего не делаем
  }

  public loadMoreFederalStandards(): void {
    console.log('Загрузка дополнительных федеральных стандартов (заглушка)');
    // Заглушка - ничего не делаем
  }

  public loadMoreBranchStandards(): void {
    console.log('Загрузка дополнительных региональных стандартов (заглушка)');
    // Заглушка - ничего не делаем
  }

  public loadMoreSectorStandards(): void {
    console.log('Загрузка дополнительных секторных стандартов (заглушка)');
    // Заглушка - ничего не делаем
  }
}
